import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';

import { NavBarComponent } from './core/components/navigation/nav-bar/nav-bar.component';
import { fadeAnimation } from './core/animations';
import { NotificationsComponent } from './core/shell/notifications/notifications.component';
import {
    ModalContainerComponent
} from './core/components/application/modal-container/modal-container.component';
import { MessageService } from 'primeng/api';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { inject as injectAnalytics } from '@vercel/analytics';
import { injectSpeedInsights } from '@vercel/speed-insights';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastModule } from 'primeng/toast';
import { IntercomService } from './intercom/intercom.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-root',
    imports: [
    CommonModule,
    RouterOutlet,
    NavBarComponent,
    NotificationsComponent,
    ModalContainerComponent,
    ToastModule
],
    templateUrl: './app.component.html',
    animations: [fadeAnimation],
    providers: [MessageService]
})
export class AppComponent implements OnInit {
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);
    private readonly googleTagManagerService = inject(GoogleTagManagerService);
    private readonly intercomService = inject(IntercomService);
    private readonly translateService = inject(TranslateService);

    public readonly title = 'planmeister-site';

    // ----------[ Dependencies ]----------

    constructor() {
        this.intercomService.onUnreadCountChange((count) => {
            console.log('Unread messages:', count);
        });

        injectAnalytics();

        const speedInsights = injectSpeedInsights({
            framework: 'angular'
        })

        // detect route changes and set the speed insights route
        this.router.events.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((event) => {
            if (event instanceof NavigationEnd) {
                speedInsights?.setRoute(event.url);
            }
        });

        this.googleTagManagerService.addGtmToDom();
        this.translateService.setDefaultLang('nl');
        this.translateService.use('nl');
    }

    // ----------[ Lifecycle Hooks ]----------

    public ngOnInit() {
        const updateViewportUnits = () => {
            const vh = window.innerHeight * 0.01;
            const vw = window.innerWidth * 0.01;
            document.documentElement.style.setProperty('--vh', `${vh}px`);
            document.documentElement.style.setProperty('--vw', `${vw}px`);
        }

        window.addEventListener('resize', updateViewportUnits);

        updateViewportUnits();
    }

    // ----------[ Template Helpers ]----------

    protected prepareRoute(outlet: RouterOutlet) {
        return outlet && outlet.activatedRouteData && outlet.activatedRouteData['animation'];
    }
}
